import React, { useEffect, useState } from "react";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { PageLoader } from "../../../components/loader/Loader";
import {
  useSafetyFacilityRoundsStore,
  ChecklistItem,
  ResponseType,
} from "../../../store/SafetyFacilityRoundsStore";

const RESPONSE_TYPE_OPTIONS: Array<{ value: ResponseType; label: string }> = [
  { value: "yesno", label: "Yes/No" },
  { value: "passfail", label: "Pass/Fail" },
  { value: "numeric", label: "Numeric (Range-based)" },
  { value: "rating", label: "Rating (1-5)" },
  { value: "text", label: "Text Remarks" },
  { value: "photo", label: "Photo Mandatory" },
];

///Sample Data
const getSampleItems = (): ChecklistItem[] => [
  {
    id: uuidv4(),
    category: "Fire Safety",
    questionText: "Fire exits unobstructed?",
    responseType: "yesno",
    weightage: 25,
    isCritical: true,
  },
  {
    id: uuidv4(),
    category: "Infection Control",
    questionText: "Hand sanitizers available?",
    responseType: "yesno",
    weightage: 20,
    isCritical: true,
  },
  {
    id: uuidv4(),
    category: "Equipment",
    questionText: "Defibrillator operational?",
    responseType: "yesno",
    weightage: 20,
    isCritical: true,
  },
  {
    id: uuidv4(),
    category: "Fire Safety",
    questionText: "Fire extinguisher inspection date valid?",
    responseType: "yesno",
    weightage: 15,
    isCritical: false,
  },
  {
    id: uuidv4(),
    category: "Infection Control",
    questionText: "Surface cleanliness rating",
    responseType: "rating",
    expectedValue: 4,
    weightage: 10,
    isCritical: false,
  },
  {
    id: uuidv4(),
    category: "Equipment",
    questionText: "Equipment temperature reading",
    responseType: "numeric",
    expectedValue: { min: 18, max: 24 },
    weightage: 10,
    isCritical: false,
  },
];

interface ChecklistItemRowProps {
  item: ChecklistItem;
  idx: number;
  onChange: (idx: number, field: keyof ChecklistItem, value: any) => void;
  onRemove: (idx: number) => void;
  isViewMode?: boolean;
  allItems: ChecklistItem[];
  onAddConditional?: (idx: number) => void;
  isConditional?: boolean;
  parentQuestion?: string;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = ({
  item,
  idx,
  onChange,
  onRemove,
  isViewMode = false,
  allItems,
  onAddConditional,
  isConditional = false,
  parentQuestion,
}) => {
  const { categories } = useSafetyFacilityRoundsStore();

  const renderExpectedValueInput = () => {
    if (isViewMode) {
      if (item.responseType === "numeric" && typeof item.expectedValue === "object") {
        return (
          <span className="text-sm text-gray-600">
            {item.expectedValue.min} - {item.expectedValue.max}
          </span>
        );
      }
      if (item.responseType === "rating" && typeof item.expectedValue === "number") {
        return <span className="text-sm text-gray-600">{item.expectedValue}</span>;
      }
      return <span className="text-sm text-gray-400">-</span>;
    }

    switch (item.responseType) {
      case "numeric":
        return (
          <div className="flex gap-2 items-center">
            <input
              type="number"
              className="border rounded px-2 py-1 w-20 text-sm focus:outline-none focus:ring-0"
              placeholder="Min"
              value={
                typeof item.expectedValue === "object" ? item.expectedValue.min : ""
              }
              onChange={(e) =>
                onChange(idx, "expectedValue", {
                  min: Number(e.target.value) || 0,
                  max:
                    typeof item.expectedValue === "object"
                      ? item.expectedValue.max
                      : 100,
                })
              }
            />
            <span className="text-gray-500">-</span>
            <input
              type="number"
              className="border rounded px-2 py-1 w-20 text-sm focus:outline-none focus:ring-0"
              placeholder="Max"
              value={
                typeof item.expectedValue === "object" ? item.expectedValue.max : ""
              }
              onChange={(e) =>
                onChange(idx, "expectedValue", {
                  min:
                    typeof item.expectedValue === "object"
                      ? item.expectedValue.min
                      : 0,
                  max: Number(e.target.value) || 100,
                })
              }
            />
          </div>
        );
      case "rating":
        return (
          <input
            type="number"
            min="1"
            max="5"
            className="border rounded px-2 py-1 w-20 text-sm focus:outline-none focus:ring-0"
            placeholder="1-5"
            value={typeof item.expectedValue === "number" ? item.expectedValue : ""}
            onChange={(e) =>
              onChange(idx, "expectedValue", Number(e.target.value) || 1)
            }
          />
        );
      default:
        return <span className="text-sm text-gray-400">-</span>;
    }
  };

  const canAddConditional =
    !isViewMode &&
    onAddConditional &&
    !isConditional &&
    (item.responseType === "yesno" || item.responseType === "passfail");

  return (
    <div
      className={`mb-2 rounded-md ${
        isConditional
          ? "ml-6 border-l-2 border-blue-300 bg-blue-50"
          : "bg-gray-50"
      }`}
    >
      {isConditional && (
        <div className="px-4 pt-2 text-xs text-blue-700 font-semibold">
          Conditional (Shows if: {item.conditionalQuestion?.condition === "no" ? "No" : item.conditionalQuestion?.condition === "fail" ? "Fail" : item.conditionalQuestion?.condition})
        </div>
      )}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 items-center border-t">
        <div className={`${isViewMode ? "col-span-2" : "col-span-1"} flex flex-col justify-center`}>
          {isViewMode ? (
            <span className="text-sm text-gray-700">{item.category}</span>
          ) : (
            <select
              className="border rounded px-2 pr-8 py-2 w-full focus:outline-none focus:ring-0 appearance-none bg-white text-sm"
              value={item.category}
              onChange={(e) => onChange(idx, "category", e.target.value)}
              required
              style={{
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg width=\'16\' height=\'16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M4 6l4 4 4-4\' stroke=\'%2361748e\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1.25em 1.25em",
              }}
            >
              <option value="">Select</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="col-span-3 flex flex-col justify-center">
          <textarea
            className="border rounded px-2 py-2 w-full focus:outline-none focus:ring-0 text-sm resize-none"
            placeholder="Enter question text"
            value={item.questionText || ""}
            onChange={(e) => onChange(idx, "questionText", e.target.value)}
            required
            disabled={isViewMode}
            rows={2}
            style={{ minHeight: "38px", maxHeight: "60px" }}
          />
        </div>
        <div className="col-span-2 flex flex-col justify-center">
          {isViewMode ? (
            <span className="text-sm text-gray-700">
              {RESPONSE_TYPE_OPTIONS.find((opt) => opt.value === item.responseType)
                ?.label || item.responseType}
            </span>
          ) : (
            <select
              className="border rounded px-2 pr-8 py-2 w-full focus:outline-none focus:ring-0 appearance-none bg-white text-sm"
              value={item.responseType}
              onChange={(e) => {
                onChange(idx, "responseType", e.target.value as ResponseType);
                if (e.target.value !== "numeric" && e.target.value !== "rating") {
                  onChange(idx, "expectedValue", undefined);
                }
              }}
              required
              style={{
                backgroundImage:
                  'url("data:image/svg+xml,%3Csvg width=\'16\' height=\'16\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M4 6l4 4 4-4\' stroke=\'%2361748e\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E")',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 0.75rem center",
                backgroundSize: "1.25em 1.25em",
              }}
            >
              <option value="">Select</option>
              {RESPONSE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="col-span-2 flex flex-col justify-center">
          {renderExpectedValueInput()}
        </div>
        <div className="col-span-1 flex flex-col justify-center">
          <input
            type="number"
            className="border rounded px-2 py-2 w-full focus:outline-none focus:ring-0 text-center text-sm"
            placeholder="%"
            value={item.weightage}
            min={0}
            max={100}
            onChange={(e) => onChange(idx, "weightage", Number(e.target.value) || 0)}
            required
            disabled={isViewMode}
          />
        </div>
        <div className="col-span-1 flex items-center justify-center">
          <input
            type="checkbox"
            className="w-5 h-5 cursor-pointer"
            checked={item.isCritical}
            onChange={(e) => onChange(idx, "isCritical", e.target.checked)}
            disabled={isViewMode}
            title="Critical Flag"
          />
        </div>
        {!isViewMode && (
          <div className="col-span-1 flex items-center justify-end gap-2 h-full">
            {canAddConditional && (
              <button
                type="button"
                onClick={() => onAddConditional?.(idx)}
                className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50 whitespace-nowrap"
                title="Add conditional question"
              >
                + If
              </button>
            )}
            <button
              type="button"
              className="text-red-500 hover:text-red-700 p-1 rounded transition-colors"
              onClick={() => onRemove(idx)}
              aria-label="Delete item"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

const ChecklistBuilder: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: urlId } = useParams();

  const mode = location.pathname.includes("/view/")
    ? "view"
    : location.pathname.includes("/edit/")
    ? "edit"
    : "create";

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const {
    currentChecklist,
    loading,
    error,
    fetchChecklistById,
    createChecklist,
    updateChecklist,
    categories,
    clearError,
  } = useSafetyFacilityRoundsStore();

  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [name, setName] = useState("");
  const [success, setSuccess] = useState("");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    if (urlId && (isViewMode || isEditMode)) {
      fetchChecklistById(urlId);
    } else if (mode === "create") {
      setChecklistItems(getSampleItems());
      setSelectedCategory("Fire Safety");
    }
  }, [urlId, mode, isViewMode, isEditMode, fetchChecklistById]);

  useEffect(() => {
    if (currentChecklist) {
      setChecklistItems(currentChecklist.items || []);
      setSelectedCategory(currentChecklist.category);
      setName(currentChecklist.name);
    }
  }, [currentChecklist]);

  useEffect(() => {
    if (error) {
      setLocalError(error);
      clearError();
    }
  }, [error, clearError]);

  const handleAddItem = () => {
    const newItem: ChecklistItem = {
      questionText: "",
      category: selectedCategory || categories[0] || "",
      responseType: "yesno",
      weightage: 0,
      isCritical: false,
      id: uuidv4(),
    };
    setChecklistItems((prev) => [...prev, newItem]);
  };

  const handleRemoveItem = (idx: number) => {
    const itemToRemove = checklistItems[idx];
    setChecklistItems((prev) =>
      prev.filter(
        (item, i) =>
          i !== idx &&
          item.conditionalQuestion?.dependsOnItemId !== itemToRemove.id
      )
    );
  };

  const handleItemChange = (
    idx: number,
    field: keyof ChecklistItem,
    value: any
  ) => {
    setChecklistItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const handleAddConditionalQuestion = (parentItemIdx: number) => {
    const parentItem = checklistItems[parentItemIdx];
    if (!parentItem || !parentItem.id) return;

    const conditionalItem: ChecklistItem = {
      questionText: "",
      category: parentItem.category,
      responseType: "yesno",
      weightage: 0,
      isCritical: false,
      id: uuidv4(),
      conditionalQuestion: {
        dependsOnItemId: parentItem.id,
        condition: parentItem.responseType === "yesno" ? "no" : "fail",
        showIf: true,
      },
    };

    setChecklistItems((prev) => {
      const newItems = [...prev];
      newItems.splice(parentItemIdx + 1, 0, conditionalItem);
      return newItems;
    });
  };

  const totalWeight = checklistItems.reduce(
    (sum, item) => sum + Number(item.weightage),
    0
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isViewMode) return;

    setLocalError("");
    setSuccess("");

    if (!name.trim()) {
      return setLocalError("Please enter checklist name.");
    }
    if (!selectedCategory) {
      return setLocalError("Please select a category.");
    }
    if (checklistItems.length === 0) {
      return setLocalError("Add at least one checklist item.");
    }
    if (totalWeight !== 100) {
      return setLocalError("Total weightage must be 100%.");
    }

    const invalidItems = checklistItems.filter(
      (item) =>
        !item.questionText.trim() ||
        !item.category ||
        !item.responseType ||
        item.weightage < 0
    );
    if (invalidItems.length > 0) {
      return setLocalError("Please fill all required fields for all items.");
    }

    const invalidExpectedValues = checklistItems.filter(
      (item) =>
        (item.responseType === "numeric" &&
          (!item.expectedValue ||
            typeof item.expectedValue !== "object" ||
            !item.expectedValue.min ||
            !item.expectedValue.max)) ||
        (item.responseType === "rating" &&
          (!item.expectedValue || typeof item.expectedValue !== "number"))
    );
    if (invalidExpectedValues.length > 0) {
      return setLocalError(
        "Please set expected values for numeric and rating type items."
      );
    }

    try {
      const body = {
        name: name.trim(),
        category: selectedCategory,
        items: checklistItems.map(({ id, ...item }) => ({
          ...item,
          id: id || uuidv4(),
        })),
        isActive: true,
      };

      if (isEditMode) {
        await updateChecklist(urlId!, body);
        setSuccess("Checklist updated successfully!");
      } else {
        await createChecklist(body);
        setSuccess("Checklist created successfully!");
        setChecklistItems(getSampleItems());
        setName("");
      }

      setTimeout(() => {
        navigate("/safety-facility-rounds/checklists");
      }, 1500);
    } catch (error: any) {
      setLocalError(
        error.response?.data?.error || "Failed to save checklist."
      );
    }
  };

  if (loading && !currentChecklist && (isViewMode || isEditMode)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageLoader />
      </div>
    );
  }

  const organizedItems = checklistItems.map((item, idx) => {
    const isConditional = !!item.conditionalQuestion;
    const parentItem = isConditional
      ? checklistItems.find((i) => i.id === item.conditionalQuestion?.dependsOnItemId)
      : null;
    return {
      item,
      idx,
      isConditional,
      parentQuestion: parentItem?.questionText,
    };
  });

  return (
    <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
      <div className="flex justify-between items-center mb-3">
        <h2 className="mb-3">
          {isViewMode
            ? "View Checklist"
            : isEditMode
            ? "Edit Checklist"
            : "Create Checklist"}
        </h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="rounded-full flex justify-center items-center px-4 py-2 text-lg font-semibold bg-[#61748e] text-white"
            onClick={() => navigate("/safety-facility-rounds/checklists")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> BACK
          </button>
        </div>
      </div>
      <div className="card">
        <form
          className="bg-white rounded-t-lg shadow-md mt-2 p-8 w-full"
          id="checklist-form"
          onSubmit={handleSubmit}
        >
          {localError && (
            <div className="text-red-500 mb-4 p-3 bg-red-50 rounded">
              {localError}
            </div>
          )}
          {success && (
            <div className="text-green-600 mb-4 p-3 bg-green-50 rounded">
              {success}
            </div>
          )}

          <div className="flex gap-4 mb-6">
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">
                Checklist Name
              </label>
              <input
                type="text"
                name="name"
                className="border rounded px-3 py-2 w-full"
                placeholder="Enter checklist name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isViewMode}
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                className="border rounded px-3 py-2 w-full"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setChecklistItems((prev) =>
                    prev.map((item) => ({
                      ...item,
                      category: e.target.value,
                    }))
                  );
                }}
                disabled={isViewMode}
                required
              >
                <option value="">Select Category</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
            {currentChecklist && (
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">
                  Checklist Code
                </label>
                <input
                  type="text"
                  className="border rounded px-3 py-2 w-full bg-gray-100"
                  value={currentChecklist.checklistCode}
                  disabled
                />
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium">
                Checklist Items
              </label>
              {!isViewMode && (
                <button
                  type="button"
                  className="text-sm text-[#0fa9a6] hover:underline flex items-center gap-1"
                  onClick={handleAddItem}
                >
                  <Plus className="w-4 h-4" /> Add More
                </button>
              )}
            </div>
            <div className="rounded border border-gray-200">
              <div className="grid grid-cols-12 gap-4 bg-gray-100 px-4 py-3 font-semibold text-sm items-center">
                <div className={isViewMode ? "col-span-2" : "col-span-1"}>Category</div>
                <div className="col-span-3">Question Text</div>
                <div className="col-span-2">Response Type</div>
                <div className="col-span-2">Expected Value</div>
                <div className="col-span-1 text-center">Weightage</div>
                <div className="col-span-1 text-center">Critical</div>
                {!isViewMode && <div className="col-span-1 text-center">Actions</div>}
              </div>
              {checklistItems.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No items added yet. Click "Add More" to add items.</p>
                </div>
              ) : (
                organizedItems.map(({ item, idx, isConditional, parentQuestion }) => (
                  <ChecklistItemRow
                    key={item.id || idx}
                    item={item}
                    idx={idx}
                    onChange={handleItemChange}
                    onRemove={handleRemoveItem}
                    onAddConditional={handleAddConditionalQuestion}
                    isViewMode={isViewMode}
                    allItems={checklistItems}
                    isConditional={isConditional}
                    parentQuestion={parentQuestion}
                  />
                ))
              )}
            </div>
            <div className="mt-2 text-sm text-gray-600">
              Total Weightage:{" "}
              <span
                className={`font-semibold ${
                  totalWeight === 100 ? "text-green-600" : "text-red-600"
                }`}
              >
                {totalWeight}%
              </span>
              {totalWeight !== 100 && (
                <span className="text-red-500 ml-2">(Must be 100%)</span>
              )}
            </div>
          </div>
        </form>
      </div>
      <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
        {!isViewMode && (
          <button
            type="submit"
            form="checklist-form"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || totalWeight !== 100}
          >
            {loading ? "Saving..." : isEditMode ? "Update Checklist" : "Save Checklist"}
          </button>
        )}
        {isViewMode && (
          <Link
            to="/safety-facility-rounds/checklists"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            Back To Checklists
          </Link>
        )}
      </div>
    </div>
  );
};

export default ChecklistBuilder;
